"use client";

import { useEffect, useRef, useState } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";

/**
 * The search chrome, shared by the real screen and the demo.
 *
 * These two surfaces drifted: one had a segmented bar and a small Filters
 * button, the other still had a plain box with two buttons under it, and the
 * Filters control was a different height and radius in each. Two search
 * experiences is how a product ends up with two sets of bugs, so the shell
 * lives here once and both sides pass their own behaviour into it.
 *
 * Only the chrome is shared. What the controls *do* is not: the real screen
 * writes the URL query string, the demo filters fixtures in local state.
 */

/**
 * Close when something outside is touched, **without eating that touch**.
 *
 * The obvious way to do this is a full-screen invisible button behind the
 * panel, and it is what this used to do. It costs the user a tap: the first
 * one lands on the scrim and only closes the panel, so reaching for the other
 * field means tapping it twice. Listening on the document instead lets the
 * same tap close the panel *and* land where it was aimed.
 *
 * Capture phase, so it runs before the target's own handler and the panel is
 * already closing by the time the field takes focus.
 */
export function useDismissOnOutside(
  ref: React.RefObject<HTMLElement | null>,
  onDismiss: () => void,
  active: boolean,
) {
  useEffect(() => {
    if (!active) return;
    const onPointerDown = (event: PointerEvent) => {
      if (ref.current?.contains(event.target as Node)) return;
      onDismiss();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [ref, onDismiss, active]);
}

/**
 * Wraps a trigger and its panel so a click on either counts as "inside".
 * Anything else closes it, and the click still reaches whatever it hit.
 */
export function PopoverAnchor({
  open,
  onClose,
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDismissOnOutside(ref, onClose, open);
  return (
    <div ref={ref} className={className ?? "relative"}>
      {children}
    </div>
  );
}

/**
 * A dim behind a sheet on a phone. Deliberately **not** interactive: dismissal
 * is handled by useDismissOnOutside, so this only darkens.
 */
export function SheetDim() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-40 bg-ink/40 backdrop-blur-[2px] sm:hidden"
    />
  );
}

/**
 * One Filters button, holding everything that is not a category.
 *
 * This went round twice and landed here on purpose. Price and rating were
 * briefly two inline chips, each stating its own answer, which is what Yelp
 * does. The problem is the row they sit in: it already carries a chip per
 * category, so two more turned a tidy line into four wrapped lines on a phone,
 * and the categories, which are the filter people actually reach for, got lost
 * among them.
 *
 * So the categories keep the row and everything else collapses into one button
 * at the **end** of it. The cost of collapsing, that the current answer is now
 * hidden, is paid back two ways: a count on the button, and the applied values
 * still echoed as removable chips underneath. Nothing is hidden that cannot be
 * seen without opening anything.
 */
export function FilterButton({
  count,
  expanded,
  onClick,
}: {
  count: number;
  expanded: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-haspopup="dialog"
      className={`inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border px-4 text-sm font-semibold transition duration-150 ${
        count > 0
          ? "border-accent/40 bg-accent/8 text-accent"
          : "border-line bg-surface text-body hover:border-accent/50 hover:text-ink"
      }`}
    >
      <SlidersHorizontal className="size-4" aria-hidden strokeWidth={1.8} />
      Filters
      {count > 0 && (
        <span className="inline-flex min-w-5 items-center justify-center rounded-md bg-accent px-1.5 py-0.5 font-mono text-[11px] font-bold text-on-accent">
          {count}
        </span>
      )}
    </button>
  );
}

/**
 * One filter as a slider, for the Filters panel (H56).
 *
 * **It applies when it is let go, not on every step.** A drag across the price
 * scale passes thirty-nine values, and running thirty-nine searches to land on
 * one would flood the list with answers nobody asked for. The label follows the
 * handle live, so the value is never a guess; the search runs on release, and
 * on each arrow key, because a key press is already a single deliberate step.
 */
export interface FilterSlider {
  label: string;
  /** The stop the handle sits at, from the applied filter. */
  stop: number;
  stops: number;
  /** What a stop means, in words, for the live label and for a screen reader. */
  describe: (stop: number) => string;
  onCommit: (stop: number) => void;
}

/**
 * What the Filters button opens. A bottom sheet on a phone, a popover from the
 * button's **right** edge above it, because the button sits at the end of the
 * row and a panel opening rightwards would leave the screen.
 *
 * Every slider applies as it is let go, so the results behind are always what
 * the panel says they are and there is nothing left to commit when it closes.
 * "Done" only dismisses, which is why it is not a primary button.
 */
export function FilterPanel({
  sliders,
  anySet,
  onClear,
  onClose,
}: {
  sliders: FilterSlider[];
  anySet: boolean;
  onClear: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <SheetDim />
      <div
        role="dialog"
        aria-label="Filters"
        className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-[var(--shadow-lift)] sm:absolute sm:inset-x-auto sm:bottom-auto sm:end-0 sm:top-11 sm:max-h-[min(32rem,70vh)] sm:w-80 sm:rounded-2xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-ink">Filters</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="-me-1 inline-flex size-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-page hover:text-ink"
          >
            <X className="size-4.5" aria-hidden />
          </button>
        </div>

        {sliders.map((slider) => (
          <SliderRow key={slider.label} slider={slider} />
        ))}

        <div className="mt-1 flex items-center gap-3 border-t border-line pt-4">
          {anySet && (
            <button
              type="button"
              onClick={onClear}
              className="text-sm font-semibold text-muted transition-colors hover:text-body"
            >
              Clear filters
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ms-auto inline-flex min-h-0 items-center justify-center rounded-lg border border-line bg-surface px-5 py-2 text-sm font-semibold text-ink transition-colors hover:bg-page"
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
}

function SliderRow({ slider }: { slider: FilterSlider }) {
  // The handle while it moves. Reset whenever the applied filter changes, so
  // a "Clear filters" or a removed chip moves the handle back with it.
  const [draft, setDraft] = useState(slider.stop);
  const [applied, setApplied] = useState(slider.stop);
  if (applied !== slider.stop) {
    setApplied(slider.stop);
    setDraft(slider.stop);
  }
  const commit = () => {
    if (draft !== slider.stop) slider.onCommit(draft);
  };
  const id = `filter-${slider.label.toLowerCase().replace(/\s+/g, "-")}`;
  const percent = slider.stops > 1 ? (draft / (slider.stops - 1)) * 100 : 0;
  return (
    <div className="mb-5">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-xs font-bold uppercase tracking-wide text-muted">
          {slider.label}
        </label>
        <span className="text-sm font-semibold text-ink" aria-hidden>
          {slider.describe(draft)}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={0}
        max={slider.stops - 1}
        step={1}
        value={draft}
        aria-valuetext={slider.describe(draft)}
        onChange={(e) => setDraft(Number(e.target.value))}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
        className="bookr-range w-full"
        style={{ "--range-fill": `${percent}%` } as React.CSSProperties}
      />
    </div>
  );
}

/** A labelled group inside a menu. */
export function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-5">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">
        {label}
      </h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

/** One choice in a menu. The whole width is tappable. */
export function OptionRow({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-start text-sm font-medium transition-colors ${
        selected ? "bg-accent/12 text-accent" : "text-body hover:bg-page hover:text-ink"
      }`}
    >
      {label}
      {selected && <span className="size-1.5 rounded-[2px] bg-accent" aria-hidden />}
    </button>
  );
}

/**
 * The segmented bar, in its two states.
 *
 * Segmented by the questions someone is actually asking rather than one box:
 * **Service** and **Where**, each labelled, each showing its current answer,
 * divided by a rule, with one submit at the end. "Service" because that is the
 * word the rest of the product uses for the thing being booked, and the
 * placeholder covers the three ways someone might name it.
 *
 * The submit sits at the **right edge, centred over the whole bar**, so on a
 * phone it lands on the intersection of the two stacked segments rather than
 * inside one of them. That is where the directories that do this put it, and
 * it makes the control read as belonging to the bar rather than to the Where
 * field.
 *
 * From `sm` up there is room, so the bar is open. On a phone it starts folded
 * into a trigger that says what is currently being searched, unfolds when
 * tapped, and folds again once the search runs.
 *
 * Rounded rectangles throughout (design system §3). Nothing here is a stadium.
 */
export function SearchBar({
  expanded,
  onExpand,
  onSubmit,
  foldedTitle,
  foldedHint,
  care,
  where,
}: {
  expanded: boolean;
  onExpand: () => void;
  onSubmit: () => void;
  /** What the folded trigger shows, e.g. the current keyword. */
  foldedTitle: string;
  /** The line under it, e.g. "Dentist · within 5 km". */
  foldedHint: string;
  /** The keyword input. Controlled or uncontrolled, the caller decides. */
  care: React.ReactNode;
  /** The location control. */
  where: React.ReactNode;
}) {
  const formRef = useRef<HTMLFormElement>(null);

  // Unfolding on a phone puts the cursor straight in the first field, so
  // "tap the bar, start typing" is one gesture rather than two.
  useEffect(() => {
    if (!expanded) return;
    formRef.current?.querySelector<HTMLInputElement>("input")?.focus();
  }, [expanded]);

  return (
    <>
      {!expanded && (
        <button
          type="button"
          onClick={onExpand}
          className="flex h-14 w-full items-center gap-3 rounded-2xl border border-line bg-page px-3 text-start transition-colors hover:border-accent/50 sm:hidden"
        >
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary text-on-primary">
            <Search className="size-4.5" aria-hidden strokeWidth={2} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-ink">
              {foldedTitle}
            </span>
            <span className="block truncate text-xs text-muted">{foldedHint}</span>
          </span>
        </button>
      )}

      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        role="search"
        className={`relative rounded-2xl border border-line bg-page transition-colors focus-within:border-accent/50 ${
          expanded ? "search-unfold" : "hidden sm:block"
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center">
          {/* Both segments are the same fixed-height box with centred content,
              so the two labels share a baseline despite one side also holding
              a 44px button. */}
          <div className="flex min-h-14 min-w-0 flex-1 flex-col justify-center px-5 py-2">
            <label htmlFor="q" className="block text-xs font-bold tracking-wide text-ink">
              Service
            </label>
            {care}
          </div>

          <div className="h-px w-full bg-line sm:h-9 sm:w-px" aria-hidden />

          <div className="flex min-h-14 min-w-0 flex-col justify-center py-2 pe-18 ps-5 sm:flex-1">
            <span className="block text-xs font-bold tracking-wide text-ink">Where</span>
            {where}
          </div>
        </div>

        {/* Centred over the whole bar, not inside a segment, so on a phone it
            sits on the seam between the two. */}
        <button
          type="submit"
          aria-label="Search"
          className="absolute end-2.5 top-1/2 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-xl bg-primary text-on-primary transition hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Search className="size-5" aria-hidden strokeWidth={2} />
        </button>
      </form>
    </>
  );
}

/** The keyword input, styled once so both surfaces match. */
export const SERVICE_INPUT_CLASS =
  "w-full bg-transparent text-sm text-body outline-none placeholder:text-muted";

/** The location trigger inside the Where segment, styled once. */
export function whereTriggerClass(active: boolean): string {
  return `-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-1.5 truncate rounded-md px-1 text-start text-sm transition-colors disabled:opacity-60 ${
    active ? "font-semibold text-accent" : "text-muted hover:text-body"
  }`;
}
